import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { Session } from '@supabase/supabase-js'
import { supabase, type ProfileRow, type AppRole } from '../lib/supabaseClient'

interface AuthContextValue {
  session: Session | null
  profile: ProfileRow | null
  role: AppRole | null
  loading: boolean
  signInWithOtp: (email: string) => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [profile, setProfile] = useState<ProfileRow | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let isMounted = true
    const init = async () => {
      const { data } = await supabase.auth.getSession()
      if (!isMounted) return
      setSession(data.session)
      if (!data.session) {
        // Try to recover session from URL hash (Supabase magic link adds #access_token & refresh_token)
        const hash = window.location.hash?.replace(/^#/, '')
        const params = new URLSearchParams(hash)
        const access_token = params.get('access_token') || undefined
        const refresh_token = params.get('refresh_token') || undefined
        if (access_token && refresh_token) {
          const { data: setData, error } = await supabase.auth.setSession({ access_token, refresh_token })
          if (!error && setData?.session) {
            setSession(setData.session)
            // Clean the URL hash
            window.history.replaceState({}, document.title, window.location.pathname + window.location.search)
          }
        }
      }
      setLoading(false)
    }
    init()

    const { data: sub } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession)
    })
    return () => {
      isMounted = false
      sub.subscription.unsubscribe()
    }
  }, [])

  useEffect(() => {
    let isMounted = true
    const loadProfile = async () => {
      if (!session) {
        setProfile(null)
        return
      }
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', session.user.id)
        .maybeSingle<ProfileRow>()
      if (!isMounted) return
      if (error) {
        // eslint-disable-next-line no-console
        console.warn('[Auth] failed to load profile', error)
        setProfile(null)
      } else {
        setProfile(data ?? null)
      }
    }
    loadProfile()
    return () => {
      isMounted = false
    }
  }, [session])

  const value = useMemo<AuthContextValue>(() => ({
    session,
    profile,
    role: (profile?.role as AppRole) ?? null,
    loading,
    async signInWithOtp(email: string) {
      const redirectTo = `${window.location.origin}/login`
      await supabase.auth.signInWithOtp({ email, options: { emailRedirectTo: redirectTo } })
    },
    async signOut() {
      await supabase.auth.signOut()
    },
  }), [session, profile, loading])

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}


